import React, { useEffect, useState, useRef } from "react";
import { Autocomplete, Box } from "@mui/material";
import { X, Tag } from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";
import TextField from "components/common/TextField";
import { useNavigate } from "react-router-dom";
import { useSetEmailFolder, useListEmailFolders } from "services/email";

// Keep some static examples as requested
const STATIC_EMAIL_LABELS = [
  "School",
  "Work",
  "House",
  "Appointments",
  "Finances",
  "Health",
  "Travel",
].sort();

export default ({
  label,
  emailRef,
  editable,
  variant = "default",
  onUpdate,
}: {
  label: string;
  emailRef: string;
  editable: boolean;
  variant?: "default" | "compact";
  onUpdate?: (folder: string) => void;
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [currentLabel, setCurrentLabel] = useState<string | null>(label);
  const [isEdit, setIsEdit] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [availableFolders, setAvailableFolders] = useState<string[]>([]);
  const autocompleteRef = useRef<HTMLInputElement>(null);

  // API hooks
  const { request: setEmailFolderRequest } = useSetEmailFolder();
  const { request: listFoldersRequest, data: foldersData } =
    useListEmailFolders((response) => {
      // Combine API folders with static examples
      const apiFolders = response.folders || [];
      const combinedFolders = Array.from(
        new Set([...STATIC_EMAIL_LABELS, ...apiFolders]),
      ).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      setAvailableFolders(combinedFolders);
    });

  useEffect(() => {
    if (editable && !foldersData) {
      // Load available folders from API
      listFoldersRequest({});
    }
  }, []);

  useEffect(() => {
    setCurrentLabel(label);
  }, [label]);

  useEffect(() => {
    if (isEdit) {
      setAutocompleteOpen(true);
      // Small delay to ensure the Autocomplete is fully rendered
      setTimeout(() => {
        if (autocompleteRef.current) {
          autocompleteRef.current.focus();
          // If there is a current label, select all text
          if (currentLabel) {
            autocompleteRef.current.setSelectionRange(
              0,
              autocompleteRef.current.value.length,
            );
          }
        }
      }, 100);
    }
  }, [isEdit]);

  const sx = {
    ...(!isMobile && {
      justifySelf: "end",
    }),
    ...(isMobile && {
      alignSelf: "end",
    }),
    flexShrink: 0,
  };

  const onChange = async (label: string | null) => {
    let finalLabel = label;
    if (typeof label === "string" && label.startsWith('Add "')) {
      // Extract the user-typed value from 'Add "..."'
      finalLabel = label.replace(/^Add \"/, "").replace(/\"$/, "");
    }

    if (finalLabel === null) {
      // When X is clicked, clear the label and keep the autocomplete open
      // Update local state for immediate feedback
      setCurrentLabel(null);
      setAutocompleteOpen(true);
      setTimeout(() => {
        autocompleteRef.current?.focus();
      }, 100);

      // Make API call to clear folder
      const resp = await setEmailFolderRequest({
        emailRef: emailRef,
        folder: "",
      });
      if (!resp) {
        console.error("Failed to clear email folder");
        return;
      }
      onUpdate?.("");
      return;
    }

    // Update local state for immediate feedback
    setCurrentLabel(finalLabel);
    setIsEdit(false);
    setAutocompleteOpen(false);

    // Make API call to set folder
    const resp = await setEmailFolderRequest({
      emailRef: emailRef,
      folder: finalLabel || "",
    });
    if (!resp) {
      console.error("Failed to set email folder");
      return;
    }
    onUpdate?.("");
  };

  const handleBlur = () => {
    // Close the autocomplete when user clicks away
    setAutocompleteOpen(false);
    setIsEdit(false);
  };

  if (!editable && !currentLabel) {
    return null;
  } else if (!isEdit && currentLabel) {
    if (variant === "compact") {
      // Simple clickable label for inbox list view
      return (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/inbox/email?folder=${encodeURIComponent(currentLabel)}`);
          }}
          sx={{
            background: "#f0faf7",
            color: "#2d7a5f",
            border: "1px solid #b8e6d0",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: "12px",
            height: "18px",
            lineHeight: "14px",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            maxWidth: "200px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            boxSizing: "border-box",
            cursor: "pointer",
            flexShrink: 0,
            "&:hover": {
              backgroundColor: "#ddf2e7",
            },
          }}
        >
          <Tag size={10} color="#6b7280" />
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {currentLabel}
          </span>
        </Box>
      );
    }

    // Default variant for email thread view - more square and compact
    return (
      <Box
        sx={{
          background: "#f0faf7",
          color: "#2d7a5f",
          border: "1px solid #b8e6d0",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          borderRadius: "6px",
          padding: "4px 8px",
          fontSize: "11px",
          fontWeight: 500,
          height: "26px",
          cursor: editable ? "pointer" : "default",
          "&:hover": editable
            ? {
                backgroundColor: "#ddf2e7",
              }
            : {},
          ...sx,
        }}
      >
        <Tag size={12} color="#2d7a5f" />
        <span
          style={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {currentLabel}
        </span>
        {editable && (
          <X
            size={12}
            color="#6b7280"
            style={{ cursor: "pointer", flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              setIsEdit(true);
            }}
          />
        )}
      </Box>
    );
  } else if (isEdit) {
    return (
      <Autocomplete
        freeSolo
        selectOnFocus
        size="small"
        options={availableFolders}
        defaultValue={currentLabel || undefined}
        onChange={(_, value) => onChange(value)}
        filterOptions={(options, params) => {
          const filtered = options.filter((option) =>
            option.toLowerCase().includes(params.inputValue.toLowerCase()),
          );

          const { inputValue } = params;
          const isExisting = options.some((option) => inputValue === option);
          if (inputValue !== "" && !isExisting) {
            filtered.push(`Add "${inputValue}"`);
          }

          return filtered;
        }}
        getOptionLabel={(option) => {
          if (typeof option === "string" && option.startsWith("Add ")) {
            return option.replace("Add ", "").replace(/"/g, "");
          }
          return option;
        }}
        renderOption={(props, option) => {
          const isAddOption =
            typeof option === "string" && option.startsWith("Add ");
          const { key, ...rest } =
            props as React.HTMLAttributes<HTMLLIElement> & { key: string };
          return (
            <React.Fragment key={key}>
              {isAddOption && (
                <hr
                  style={{
                    margin: "8px 0",
                    border: "none",
                    borderTop: "1px solid #e0e0e0",
                  }}
                />
              )}
              <li {...rest}>{option}</li>
            </React.Fragment>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label=""
            placeholder="Email folder"
            size="small"
            inputRef={autocompleteRef}
          />
        )}
        sx={{
          ...sx,
          width: "140px",
          "& .MuiOutlinedInput-root": {
            minHeight: "fit-content",
          },
          "& input.MuiInputBase-input": {
            marginTop: "0",
            fontSize: "12px",
          },
          "& .MuiInputLabel-root.MuiInputLabel-shrink": {
            left: "15px",
            top: "4px",
          },
          "label.MuiFormLabel-root": {
            top: "16px",
          },
        }}
        open={autocompleteOpen}
        onOpen={() => setAutocompleteOpen(true)}
        onClose={handleBlur}
        onBlur={handleBlur}
        autoFocus={isEdit}
      />
    );
  } else if (editable) {
    return (
      <Box
        onClick={() => setIsEdit(true)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          backgroundColor: "#f9fafb",
          border: "1px dashed #d1d5db",
          borderRadius: "6px",
          padding: "4px 8px",
          fontSize: "11px",
          fontWeight: 500,
          color: "#6b7280",
          height: "26px",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "#f3f4f6",
            borderColor: "#9ca3af",
          },
          ...sx,
        }}
      >
        <Tag size={12} color="#9ca3af" />
        <span>Add tag</span>
      </Box>
    );
  }

  return null;
};
